var _a;
import platform from 'platform';
import VersionHelper from '@/src/util/class/VersionHelper';
import UserAgentHelper from './UserAgentHelper';
const ANDROID_LOW_VERSION = '9';
const IOS_LOW_VERSION = '11';
export default class EnvChecker extends UserAgentHelper {
    static init(params) {
        var _b, _c;
        this.data = params !== null && params !== void 0 ? params : {
            os: ((_b = this.env.os) === null || _b === void 0 ? void 0 : _b.family) || '',
            osVersion: ((_c = this.env.os) === null || _c === void 0 ? void 0 : _c.version) || '',
        };
    }
    static isLocal() {
        return process.env.NEXT_PUBLIC_MODE === 'LOCAL';
    }
    static isDev() {
        return process.env.NEXT_PUBLIC_MODE === 'DEV';
    }
    static isProd() {
        return (process.env.NEXT_PUBLIC_MODE !== 'QA' &&
            process.env.NEXT_PUBLIC_MODE !== 'DEV' &&
            process.env.NEXT_PUBLIC_MODE !== 'LOCAL');
    }
    static isLowVersion() {
        var _b, _c;
        if (this.isApp()) {
            return Number(this.getOSVersion()) <= 28;
        }
        const version = (_c = (_b = this.data) === null || _b === void 0 ? void 0 : _b.osVersion) !== null && _c !== void 0 ? _c : '1';
        return VersionHelper.isLower(version, this.isAndroid() ? ANDROID_LOW_VERSION : IOS_LOW_VERSION);
    }
    static isApp() {
        return /VRBT_APP|VRBT_IOS_APP/i.test(this.getUserAgent());
    }
    static isMobile() {
        return this.isApp() || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(this.getUserAgent());
    }
    static isIOSApp() {
        return /VRBT_IOS_APP/i.test(this.getUserAgent());
    }
    static isAndroid() {
        return /Android/i.test(this.getUserAgent());
    }
    static isIOS() {
        var _b;
        const osFamily = ((_b = this.data) === null || _b === void 0 ? void 0 : _b.os) || '';
        return /iOS|iPhone|iPad/i.test(osFamily) || /iPhone|iPad|iOS/i.test(this.getUserAgent());
    }
    static isSamsungBrowser() {
        return /SamsungBrowser/i.test(this.getUserAgent());
    }
    static getOS() {
        if (this.isApp() || this.isAndroid())
            return 'AOS';
        if (this.isIOS())
            return 'IOS';
        return 'ETC';
    }
    static getOSVersion() {
        var _b;
        return ((_b = this.data) === null || _b === void 0 ? void 0 : _b.osVersion) || '';
    }
    static getAppVersion() {
        var _b;
        return ((_b = this.data) === null || _b === void 0 ? void 0 : _b.appVersion) || '';
    }
    static getAppVersionDigit() {
        var _b;
        const v = (((_b = this.data) === null || _b === void 0 ? void 0 : _b.appVersion) || '').replace(/\./g, '');
        return v ? parseInt(v, 10) : 0;
    }
    static getFcmToken() {
        var _b;
        return ((_b = this.data) === null || _b === void 0 ? void 0 : _b.firebaseToken) || '';
    }
    static getDeviceInfo() {
        if (!this.data)
            return {};
        const { deviceId, deviceModel, appVersion } = this.data;
        return { deviceId, deviceModel, appVersion };
    }
    static getNaturalOrientation() {
        var _b, _c;
        const w = window.innerWidth;
        const h = window.innerHeight;
        const angle = ((_c = (_b = window.screen.orientation) === null || _b === void 0 ? void 0 : _b.angle) !== null && _c !== void 0 ? _c : window.orientation) || 0;
        if (angle === 0)
            return w > h ? 'landscape' : 'portrait';
        return 'portrait';
    }
    static isUnfoldedSize(width, height) {
        const minSide = Math.min(width, height);
        const maxSide = Math.max(width, height);
        const aspect = maxSide / Math.max(1, minSide);
        return minSide >= 560 && aspect <= 2.1;
    }
    static isFoldedSize(width, height) {
        return Math.min(width, height) < 560;
    }
    static getViewportCandidates() {
        var _b, _c;
        if (typeof window === 'undefined')
            return [];
        const candidates = [{ width: window.innerWidth, height: window.innerHeight }];
        if (window.visualViewport) {
            candidates.push({
                width: Math.round(window.visualViewport.width),
                height: Math.round(window.visualViewport.height),
            });
        }
        if (((_b = window.screen) === null || _b === void 0 ? void 0 : _b.width) && ((_c = window.screen) === null || _c === void 0 ? void 0 : _c.height)) {
            candidates.push({
                width: window.screen.width,
                height: window.screen.height,
            });
        }
        return candidates.filter(({ width, height }) => width > 0 && height > 0);
    }
    static isGalaxyFoldDevice() {
        var _b;
        if (this._isFoldDeviceCached)
            return true;
        const ua = this.getUserAgent();
        const chModel = (typeof navigator !== 'undefined' && ((_b = navigator === null || navigator === void 0 ? void 0 : navigator.userAgentData) === null || _b === void 0 ? void 0 : _b.model)) || '';
        const byUA = /SM-F9\d{2}\w*|Galaxy Fold|Fold\d/i.test(ua);
        const byCH = /SM-F9\d{2}\w*/i.test(chModel);
        if (byUA || byCH) {
            this._isFoldDeviceCached = true;
            return true;
        }
        if (this.isAndroid() && typeof window !== 'undefined') {
            const w = window.innerWidth;
            const h = window.innerHeight;
            const minSide = Math.min(w, h);
            const maxSide = Math.max(w, h);
            const aspect = maxSide / Math.max(1, minSide);
            const large = minSide >= 600;
            const notUltraWide = aspect <= 2.1;
            if (large && notUltraWide) {
                this._isFoldDeviceCached = true;
                return true;
            }
        }
        return false;
    }
    static isFoldUnfoldedNow() {
        if (typeof window === 'undefined')
            return this._isUnfoldedCached;
        const candidates = this.getViewportCandidates();
        if (candidates.some(({ width, height }) => this.isUnfoldedSize(width, height))) {
            this._isUnfoldedCached = true;
        }
        else if (candidates.every(({ width, height }) => this.isFoldedSize(width, height))) {
            this._isUnfoldedCached = false;
        }
        return this._isUnfoldedCached;
    }
    static isFoldUnfolded() {
        return this.isFoldUnfoldedNow();
    }
}
_a = EnvChecker;
EnvChecker.env = platform.parse(_a.getUserAgent());
EnvChecker._isFoldDeviceCached = false;
EnvChecker._isUnfoldedCached = false;
EnvChecker.getIncmType = () => (_a.isApp() ? 'APP' : 'MWEB');
